import {
    applyHtmlSpecialChars,
    getImageDimensions,
    getImageMime,
} from "../util/";
import { CreateElement } from "./element";
import { Relationship } from "./relationship";

type MimeImage = "image/gif" | "image/jpg" | "image/png" | "image/webp";

interface OptionsImage {
    /** Alt text value */
    descr?: string;
    /** Height: EMUs value. Get from image as default */
    height?: number;
    /** Image content: png, jpg/jpeg, gif, webp formats */
    image: ArrayBuffer;
    /** Float image. Only if textWrap is not inline */
    float?: "left" | "center" | "right";
    /** Link: URL */
    link?: string;
    /** Mime type. Auto detect if not set */
    mime?: MimeImage | null;
    /** Spacing: EMUs value. Default as 0 */
    spacing?: {
        top?: number;
        right?: number;
        bottom?: number;
        left?: number;
    };
    /** Text wrapping. Default as inline */
    textWrapping?: "inline" | "square" | "front" | "back" | "topBottom";
    /** Width: EMUs value. Get from image as default */
    width?: number;
}

/**
 * Create image
 */
class CreateImage extends CreateElement {
    /**
     * Id docPr
     */
    protected idDocPr: number;

    /**
     * Id
     */
    protected id: string;

    /**
     * Id link
     */
    protected idLink?: string;

    /**
     * Element options
     */
    protected options: OptionsImage;

    /**
     * Constructor
     *
     * @param options - Options image
     * @param id - Id
     * @param idDocPr - Id docPr
     * @param idLink - Id link
     */
    constructor(
        options: OptionsImage,
        id: string,
        idDocPr: number,
        idLink?: string
    ) {
        super();
        // init options
        const imageDimensions = getImageDimensions(options.image);

        // get mime type
        options.mime ??= getImageMime(options.image) ?? "image/png";

        // sizes
        if (imageDimensions != null) {
            if (options.width == null) {
                // transform size: px => pt => EMUs
                options.width = Math.round(
                    imageDimensions.width * 0.75 * 12700
                );
            }
            if (options.height == null) {
                // transform size: px => pt => EMUs
                options.height = Math.round(
                    imageDimensions.height * 0.75 * 12700
                );
            }
        }

        // default options
        options.height ??= 1000000;
        options.spacing ??= { top: 0, right: 0, bottom: 0, left: 0 };
        options.spacing.top ??= 0;
        options.spacing.right ??= 0;
        options.spacing.bottom ??= 0;
        options.spacing.left ??= 0;
        options.textWrapping ??= "inline";
        options.width ??= 1000000;

        this.options = options;
        this.id = id;
        this.idDocPr = idDocPr;
        this.idLink = idLink;
    }

    /**
     * Creates content
     *
     * @returns Contents
     */
    public create(): string {
        // spacing
        const extendTop = this.options.spacing?.top ?? 0;
        const extendRight = this.options.spacing?.right ?? 0;
        const extendBottom = this.options.spacing?.bottom ?? 0;
        const extendLeft = this.options.spacing?.left ?? 0;

        let contents = "<w:r><w:drawing>";
        let drawingContents = '<wp:simplePos x="0" y="0"/>';
        if (this.options.textWrapping != "inline") {
            if (this.options.float != null) {
                drawingContents +=
                    '<wp:positionH relativeFrom="margin"><wp:align>' +
                    this.options.float +
                    '</wp:align></wp:positionH><wp:positionV relativeFrom="line"><wp:posOffset>0</wp:posOffset></wp:positionV>';
            } else {
                drawingContents +=
                    '<wp:positionH relativeFrom="margin"><wp:posOffset>0</wp:posOffset></wp:positionH><wp:positionV relativeFrom="line"><wp:posOffset>0</wp:posOffset></wp:positionV>';
            }
        }
        drawingContents +=
            '<wp:extent cx="' +
            this.options.width +
            '" cy="' +
            this.options.height +
            '"/>';
        drawingContents +=
            '<wp:effectExtent b="' +
            extendBottom +
            '" l="' +
            extendLeft +
            '" r="' +
            extendRight +
            '" t="' +
            extendTop +
            '"/>';
        if (
            this.options.textWrapping == "back" ||
            this.options.textWrapping == "front"
        ) {
            drawingContents += "<wp:wrapNone/>";
        } else if (this.options.textWrapping == "square") {
            drawingContents += '<wp:wrapSquare wrapText="bothSides"/>';
        } else if (this.options.textWrapping == "topBottom") {
            drawingContents += "<wp:wrapTopAndBottom/>";
        }
        drawingContents +=
            '<wp:docPr id="' +
            this.idDocPr +
            '" name="Picture ' +
            this.idDocPr +
            '" ';
        if (this.options.descr != null) {
            drawingContents +=
                ' descr="' + applyHtmlSpecialChars(this.options.descr) + '" ';
        }
        drawingContents += ">";
        if (this.options.link != null && this.idLink != null) {
            drawingContents +=
                '<a:hlinkClick r:id="rId' +
                this.idLink +
                '" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"/>';
        }
        drawingContents += "</wp:docPr>";
        drawingContents +=
            '<wp:cNvGraphicFramePr><a:graphicFrameLocks noChangeAspect="1" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"/></wp:cNvGraphicFramePr>';
        drawingContents +=
            '<a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">';
        drawingContents +=
            '<pic:nvPicPr><pic:cNvPr id="0" name="Picture ' +
            this.idDocPr +
            '"/><pic:cNvPicPr/></pic:nvPicPr>';
        drawingContents +=
            '<pic:blipFill><a:blip cstate="print" r:embed="rId' +
            this.id +
            '"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill>';
        drawingContents +=
            '<pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="' +
            this.options.width +
            '" cy="' +
            this.options.height +
            '"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr>';
        drawingContents += "</pic:pic></a:graphicData></a:graphic>";

        switch (this.options.textWrapping) {
            case "back":
                contents +=
                    '<wp:anchor allowOverlap="1" behindDoc="1" distB="0" distL="0" distR="0" distT="0" layoutInCell="1" locked="0" relativeHeight="0" simplePos="0">' +
                    drawingContents +
                    "</wp:anchor>";
                break;
            case "front":
                contents +=
                    '<wp:anchor allowOverlap="1" behindDoc="0" distB="0" distL="0" distR="0" distT="0" layoutInCell="1" locked="0" relativeHeight="0" simplePos="0">' +
                    drawingContents +
                    "</wp:anchor>";
                break;
            case "square":
                contents +=
                    '<wp:anchor allowOverlap="1" behindDoc="0" distB="0" distL="0" distR="0" distT="0" layoutInCell="1" locked="0" relativeHeight="0" simplePos="0">' +
                    drawingContents +
                    "</wp:anchor>";
                break;
            case "topBottom":
                contents +=
                    '<wp:anchor allowOverlap="1" behindDoc="0" distB="0" distL="0" distR="0" distT="0" layoutInCell="1" locked="0" relativeHeight="0" simplePos="0">' +
                    drawingContents +
                    "</wp:anchor>";
                break;
            case "inline":
            default:
                contents +=
                    '<wp:inline distB="0" distL="0" distR="0" distT="0">' +
                    drawingContents +
                    "</wp:inline>";
                break;
        }

        contents += "</w:drawing></w:r>";

        // keep image external relationship
        const newExternalRelationship: Relationship = {
            id: "rId" + this.id,
            target:
                "media/" + this.id + "." + this.getRelationshipFileExtension(),
            type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/image",
        };
        this.externalRelationships.push(newExternalRelationship);

        // keep default from the element
        this.externalDefaults.set(
            this.getRelationshipFileExtension(),
            this.getMime()
        );

        // keep file from the element
        this.fileContents.set(
            "word/media/" + this.id + "." + this.getRelationshipFileExtension(),
            this.options.image
        );

        // keep image link external relationship
        if (this.options.link != null && this.idLink != null) {
            const newExternalRelationshipLink: Relationship = {
                id: "rId" + this.idLink,
                target: applyHtmlSpecialChars(this.options.link),
                targetMode: "External",
                type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink",
            };
            this.externalRelationships.push(newExternalRelationshipLink);
        }

        return contents;
    }

    /**
     * Getter mime
     *
     * @returns Image options
     */
    public getMime(): MimeImage {
        return this.options.mime ?? "image/png";
    }

    /**
     * Getter options
     *
     * @returns Image options
     */
    public getOptions(): OptionsImage {
        return this.options;
    }

    /**
     * Gets file extension to be added as relationship
     *
     * @returns File extension. Default as png
     */
    public getRelationshipFileExtension(): string {
        return this.options.mime?.replace("image/", "") ?? "png";
    }
}

export { CreateImage, MimeImage, OptionsImage };
